import { useState } from "react";
import { Button } from "@/components/ui/button";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Undo2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

interface UndoQuoteSendButtonProps {
  leadId: string;
  currentStatus: string;
}

export const UndoQuoteSendButton = ({ leadId, currentStatus }: UndoQuoteSendButtonProps) => {
  const [showDialog, setShowDialog] = useState(false);
  const [reason, setReason] = useState("");
  const queryClient = useQueryClient();

  const undoQuoteMutation = useMutation({
    mutationFn: async () => {
      if (!reason.trim()) {
        throw new Error("Please provide a reason for undoing the quote send");
      }

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("User not authenticated");

      // Update lead status back to quote_generated
      const { error } = await supabase
        .from("leads")
        .update({ status: "quote_generated" })
        .eq("id", leadId);

      if (error) throw error;

      // Log the activity with reason
      await supabase.from("activity_log").insert({
        lead_id: leadId,
        user_id: user.id,
        action: "Quote Send Undone",
        details: `Quote send reversed. Reason: ${reason}`,
      });

      // Log the status change
      await supabase.from("lead_changes").insert({
        lead_id: leadId,
        user_id: user.id,
        field_changed: "status",
        old_value: currentStatus,
        new_value: "quote_generated",
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["lead", leadId] });
      queryClient.invalidateQueries({ queryKey: ["leads"] });
      queryClient.invalidateQueries({ queryKey: ["activity-log", leadId] });
      queryClient.invalidateQueries({ queryKey: ["lead-changes", leadId] });
      toast.success("Quote send has been undone");
      setShowDialog(false);
      setReason("");
    },
    onError: (error: any) => {
      toast.error(`Failed to undo: ${error.message}`);
    },
  });

  // Only show button if status is quote_sent
  if (currentStatus !== "quote_sent") return null;

  return (
    <>
      <Button 
        onClick={() => setShowDialog(true)}
        className="gap-2"
        variant="outline"
      >
        <Undo2 className="h-4 w-4" />
        Undo Quote Send
      </Button>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Undo Quote Send</DialogTitle>
            <DialogDescription>
              This will revert the lead status back to "Quote Generated". Please provide a reason for this change.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-4">
            <Label htmlFor="reason">Reason for undoing</Label>
            <Textarea
              id="reason"
              placeholder="e.g., Quote was sent by mistake, client requested changes..."
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={4}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => undoQuoteMutation.mutate()}
              disabled={undoQuoteMutation.isPending || !reason.trim()}
            >
              {undoQuoteMutation.isPending ? "Undoing..." : "Confirm Undo"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
